import { queryOptions, useQuery } from '@tanstack/react-query';
import { z } from 'zod';
import { parentChildReportTemplatesListPath } from '~/hooks/analyticsStatAccess/parentChildReportTemplatesApiPaths';
import {
  staffConfigModeQueryKeyPart,
  staffConfigModeQueryParams,
} from '~/hooks/analyticsStatAccess/statAccessQueryScope';
import type { Genie2ReportingMode } from '~/utils/analyticsReportingMode';
import { customFetch } from '~/utils/customFetch';

const assignabilitySchema = z.object({
  hasAccountLimit: z.boolean(),
  alreadyAssigned: z.boolean(),
  canAssign: z.boolean(),
  /** Child copy id for POST /unassign. Set when alreadyAssigned; otherwise null. */
  childAssignedPublicId: z.string().nullable(),
});

/**
 * Parent Default + child assignability (picker for re-share).
 * Ergon uses assignedPublicId / libraryPublicId (not staff library publicId).
 */
const parentChildReportTemplateSchema = z.object({
  assignedPublicId: z.string(),
  libraryPublicId: z.string(),
  statKey: z.string(),
  name: z.string().nullable(),
  description: z.string().nullable(),
  selection: z.object({
    columnLayout: z.unknown(),
  }),
  filters: z.unknown(),
  sort: z.unknown(),
  createdAt: z.string(),
  createdBy: z.string(),
  updatedAt: z.string().optional(),
  updatedBy: z.string().nullable().optional(),
  assignability: assignabilitySchema,
});

const parentChildReportTemplateListSchema = z.array(parentChildReportTemplateSchema);

export type ParentChildReportTemplate = z.infer<typeof parentChildReportTemplateSchema>;
export type ParentChildReportTemplatesResponse = { templates: ParentChildReportTemplate[] };

function parseParentChildReportTemplatesList(data: unknown): ParentChildReportTemplate[] {
  if (Array.isArray(data)) {
    return parentChildReportTemplateListSchema.parse(data);
  }
  return z.object({ templates: parentChildReportTemplateListSchema }).parse(data).templates;
}

const fetchParentChildReportTemplates = async (
  parentAccountId: string,
  childClientId: string,
  statKey?: string,
  analyticsReportingMode?: Genie2ReportingMode | null,
): Promise<ParentChildReportTemplatesResponse> => {
  const { data } = await customFetch.get<unknown>(
    parentChildReportTemplatesListPath(parentAccountId, childClientId),
    {
      headers: { 'Content-Type': 'application/json' },
      useNodeEndpoint: true,
      params: staffConfigModeQueryParams(analyticsReportingMode, statKey ? { statKey } : undefined),
    },
  );
  return { templates: parseParentChildReportTemplatesList(data) };
};

export const parentChildReportTemplatesQueryKey = (
  parentAccountId: string,
  childClientId: string,
) => ['parent-child', 'report-templates', parentAccountId, childClientId] as const;

export const getParentChildReportTemplatesQueryOptions = (
  parentAccountId: string,
  childClientId: string,
  statKey?: string,
  analyticsReportingMode?: Genie2ReportingMode | null,
) =>
  queryOptions({
    queryKey: [
      ...parentChildReportTemplatesQueryKey(parentAccountId, childClientId),
      statKey,
      staffConfigModeQueryKeyPart(analyticsReportingMode),
    ],
    queryFn: () =>
      fetchParentChildReportTemplates(
        parentAccountId,
        childClientId,
        statKey,
        analyticsReportingMode,
      ),
    staleTime: 60 * 1000,
    refetchOnWindowFocus: false,
  });

export const useParentChildReportTemplatesQuery = (
  parentAccountId: string,
  childClientId: string,
  statKey?: string,
  analyticsReportingMode?: Genie2ReportingMode | null,
) =>
  useQuery({
    ...getParentChildReportTemplatesQueryOptions(
      parentAccountId,
      childClientId,
      statKey,
      analyticsReportingMode,
    ),
    enabled: !!parentAccountId && !!childClientId,
  });
